import { isEqual } from 'radash';
import { subscribe } from 'valtio';

import { SelectionService } from '@diagram/services/SelectionService';
import { state as selectionState } from '@diagram/state/selection';
import { EDIT_SIDEBAR_TYPES, state as sidebarState } from '@diagram/state/sidebar';
import { SidebarHistoryEntry, state as sidebarHistoryState } from '@diagram/state/sidebar-history';

import { state as currentProjectState } from 'state/current-project';

const MAX_ENTRIES = 50;
// Fix for sidebars that set their default tab right after mounting
const HISTORY_DELAY = 300;

const isSameLocation = (a: SidebarHistoryEntry, b: SidebarHistoryEntry) => {
    return a.type === b.type && isEqual(a.selection, b.selection);
};

const SidebarHistoryService = {
    updatedAt: 0,

    snapshotEntry: (): SidebarHistoryEntry => {
        return {
            type: sidebarState.type,
            tab: sidebarState.tab,
            selection: EDIT_SIDEBAR_TYPES.includes(sidebarState.type)
                ? selectionState.selection.map((item) => ({ ...item }))
                : [],
        };
    },

    record: () => {
        if (!currentProjectState.project?.id) {
            return;
        }

        const entry = SidebarHistoryService.snapshotEntry();

        if (EDIT_SIDEBAR_TYPES.includes(entry.type) && entry.selection.length === 0) {
            return;
        }

        const current = sidebarHistoryState.entries[sidebarHistoryState.index];

        if (current && isEqual(current, entry)) {
            return;
        }

        if (current && isSameLocation(current, entry)) {
            if (current.tab === null || Date.now() - SidebarHistoryService.updatedAt < HISTORY_DELAY) {
                sidebarHistoryState.entries[sidebarHistoryState.index] = entry;
                SidebarHistoryService.updatedAt = Date.now();

                return;
            }
        }

        const entries = sidebarHistoryState.entries.slice(0, sidebarHistoryState.index + 1);

        entries.push(entry);

        if (entries.length > MAX_ENTRIES) {
            entries.shift();
        }

        sidebarHistoryState.entries = entries;
        sidebarHistoryState.index = entries.length - 1;
        SidebarHistoryService.updatedAt = Date.now();
    },

    canGoBack: () => {
        return sidebarHistoryState.index > 0;
    },

    canGoForward: () => {
        return sidebarHistoryState.index < sidebarHistoryState.entries.length - 1;
    },

    back: () => {
        if (!SidebarHistoryService.canGoBack()) {
            return;
        }

        sidebarHistoryState.index -= 1;
        SidebarHistoryService.restore(sidebarHistoryState.entries[sidebarHistoryState.index]);
    },

    forward: () => {
        if (!SidebarHistoryService.canGoForward()) {
            return;
        }

        sidebarHistoryState.index += 1;
        SidebarHistoryService.restore(sidebarHistoryState.entries[sidebarHistoryState.index]);
    },

    restore: (entry: SidebarHistoryEntry) => {
        sidebarState.type = entry.type;
        sidebarState.tab = entry.tab;
        sidebarState.collapsed = false;

        SelectionService.select(entry.selection.map((item) => ({ ...item })));

        SidebarHistoryService.updatedAt = Date.now();
    },

    reset: () => {
        sidebarHistoryState.entries = [];
        sidebarHistoryState.index = -1;
    },
};

// `sidebarState`/`selectionState` and this module form a circular import (sidebar →
// SidebarService → SidebarHistoryService → sidebar). Registering the subscriptions synchronously
// would read `sidebarState` while `sidebar.ts` is still evaluating its imports, before its
// `export const state` runs, throwing "Cannot access 'state' before initialization". Deferring to
// a microtask lets the whole module graph finish initializing first, regardless of chunk
// evaluation order.
if (typeof window !== 'undefined') {
    queueMicrotask(() => {
        subscribe(sidebarState, SidebarHistoryService.record);
        subscribe(selectionState, SidebarHistoryService.record);
    });
}

export { SidebarHistoryService };
